// ============================================================
// menuUi.tsx
// The shared RIGHT-CLICK / ⋯ menu language (approved mockup "CA6261" —
// casevo-context-menu-redesign.html). One surface + row vocabulary for
// every context menu (CaseContextMenu, CalendarContextMenu), matching the
// app's popovers: the notification dropdown (NotificationsPanel.tsx:209-212)
// and the My Work popover (MyWorkParts.tsx:31-33 — same shadow, 12px radius,
// 6px inset, rounded hover rows on bg-casevo-input).
// Styling only — no behaviour lives here.
// ============================================================
import React from 'react'

// Mockup .menu (:39) — 272px, surface, 1px border, 12px radius, 6px inset,
// the popover shadow (:11 light / :13 dark), forest text at 13.5px.
export const MENU_SURFACE =
  'w-[272px] bg-casevo-surface border border-casevo-border rounded-[12px] p-[6px] text-casevo-forest text-[13.5px] leading-[normal] ' +
  'shadow-[0_18px_44px_rgba(10,25,17,0.20),0_2px_6px_rgba(10,25,17,0.08)] dark:shadow-[0_18px_44px_rgba(0,0,0,0.5),0_2px_6px_rgba(0,0,0,0.3)]'

// Mockup .mi (:46-58). Row: 16px icon + label, 10px gap, 7.5/10 padding,
// 8px radius, hover/focus = bg-casevo-input (+ the casevo-focus ring on
// keyboard focus, :51).
const ROW_BASE =
  'w-full flex items-center gap-[10px] px-[10px] py-[7.5px] rounded-[8px] text-left whitespace-nowrap leading-[1.2] ' +
  'focus:outline-none casevo-focus transition-colors'
export type MenuTone = 'default' | 'danger' | 'status'
const ROW_TONE: Record<MenuTone, string> = {
  default: 'text-casevo-forest hover:bg-casevo-input focus-visible:bg-casevo-input',
  // :55-57 — danger text + soft red tint on hover.
  danger: 'text-casevo-danger hover:bg-[#fdecec] focus-visible:bg-[#fdecec] dark:hover:bg-[rgba(252,165,165,0.12)] dark:focus-visible:bg-[rgba(252,165,165,0.12)]',
  // :58 — status transitions: neutral label, brand-tinted icon.
  status: 'text-casevo-forest hover:bg-casevo-input focus-visible:bg-casevo-input',
}
const ICON_TONE: Record<MenuTone, string> = {
  default: 'text-casevo-mid',
  danger: 'text-casevo-danger',
  status: 'text-casevo-brand dark:text-[#8fd6b0]',
}

// Feather paths (design-language §6 — inline SVG, no icon library).
const PATHS = {
  arrow: <><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></>,
  tab: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18" /><path d="M8 4v5" /></>,
  external: <><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></>,
  user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  star: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />,
  edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
  assign: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6" /><path d="M22 11h-6" /></>,
  check: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="m22 4-10 10.01-3-3" /></>,
  link: <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>,
  layers: <><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></>,
  pdf: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 15h6" /><path d="M9 11h2" /></>,
  csv: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M8 13h8" /><path d="M8 17h8" /><path d="M12 11v8" /></>,
  copy: <><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>,
  trash: <><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>,
  x: <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>,
  download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><path d="M12 15V3" /></>,
  briefcase: <><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></>,
  checkSquare: <><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>,
  flag: <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><path d="M4 22v-7" /></>,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /></>,
  bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>,
  note: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M16 13H8" /><path d="M16 17H8" /></>,
  panel: <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M15 3v18" /></>,
  day: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M3 10h18" /><path d="M10 14h4v4h-4z" /></>,
  move: <><polyline points="5 9 2 12 5 15" /><polyline points="9 5 12 2 15 5" /><polyline points="15 19 12 22 9 19" /><polyline points="19 9 22 12 19 15" /><path d="M2 12h20" /><path d="M12 2v20" /></>,
  target: <><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" /></>,
} as const
export type MenuIconName = keyof typeof PATHS

export const MenuIcon: React.FC<{ name: MenuIconName; size?: number; filled?: boolean; className?: string }> = ({
  name, size = 16, filled = false, className = '',
}) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill={filled ? 'currentColor' : 'none'} stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`shrink-0 ${className}`}>
    {PATHS[name]}
  </svg>
)

export interface MenuItemProps {
  icon?: React.ReactNode       // a <MenuIcon/> (or any 16px node)
  label: React.ReactNode
  onClick?: () => void
  tone?: MenuTone
  iconClassName?: string       // override the icon colour (e.g. the gold favourite star)
  trailing?: React.ReactNode   // right-aligned hint (mockup .tr :48-49)
  disabled?: boolean
  disabledReason?: string      // mockup .why (:54) — shown right-aligned when disabled
}

// One menu row (mockup .mi). Disabled = soft text at 55% opacity (:52-53).
export const MenuItem: React.FC<MenuItemProps> = ({
  icon, label, onClick, tone = 'default', iconClassName, trailing, disabled = false, disabledReason,
}) => (
  <button
    type="button"
    role="menuitem"
    disabled={disabled}
    aria-disabled={disabled || undefined}
    title={disabled ? disabledReason : undefined}
    onClick={disabled ? undefined : onClick}
    className={`${ROW_BASE} ${disabled ? 'text-casevo-text-soft opacity-[0.55] cursor-not-allowed' : ROW_TONE[tone]}`}
  >
    {icon && <span className={`inline-flex ${disabled ? 'text-casevo-text-soft' : iconClassName ?? ICON_TONE[tone]}`}>{icon}</span>}
    <span>{label}</span>
    {disabled && disabledReason ? (
      <span className="ml-auto text-[10.5px] text-casevo-text-soft">{disabledReason}</span>
    ) : trailing ? (
      <span className="ml-auto inline-flex items-center gap-[4px] text-[11px] text-casevo-text-soft">{trailing}</span>
    ) : null}
  </button>
)

// Mockup .sep (:60) — soft hairline, 5px vertical / 4px horizontal inset.
export const MenuSep: React.FC = () => <div role="separator" className="h-px bg-casevo-border-soft my-[5px] mx-[4px]" />

// Mockup .grp (:61) — uppercase micro group heading.
export const MenuGroupHead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[10.5px] tracking-[0.16em] uppercase text-casevo-text-soft font-semibold px-[10px] pt-[8px] pb-[4px]">{children}</div>
)

// Mockup .m-head (:40-45) — context header: 30px mint icon tile + title/ref,
// or a count pill (multi-select). Display-only.
export const MenuHeader: React.FC<{ icon: MenuIconName; title: React.ReactNode; reference?: string; count?: string }> = ({
  icon, title, reference, count,
}) => (
  <div className="flex items-center gap-[10px] px-[10px] pt-[8px] pb-[10px] mb-[6px] border-b border-casevo-border-soft">
    <span className="w-[30px] h-[30px] rounded-[9px] grid place-items-center bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0] shrink-0">
      <MenuIcon name={icon} />
    </span>
    <div className="min-w-0">
      <div className="font-bold text-[13px] leading-[1.25] whitespace-nowrap overflow-hidden text-ellipsis">{title}</div>
      {reference && <div className="font-mono text-[10.5px] text-casevo-text-soft tracking-[0.02em]">{reference}</div>}
    </div>
    {count && (
      <span className="ml-auto text-[11px] font-bold text-casevo-brand dark:text-[#8fd6b0] bg-casevo-mint rounded-full px-[8px] py-[1px]">{count}</span>
    )}
  </div>
)
